import type { AppProps, NextWebVitalsMetric } from 'next/app';
import Head from 'next/head';

import { i18n } from '@admin/i18n';
import { FlashMessageHolder } from '@admin/molecules/FlashMessage';
import { Navigation } from '@admin/templates/Navigation';
import { I18nProvider } from '@cms/i18n/client';
import { SupertokensWrapper } from '@common/atoms/SupertokensWrapper';
import { ThemeFonts } from '@common/atoms/ThemeFonts/ThemeFonts.client';
import { ThemeProvider } from '@common/atoms/ThemeProvider';
import { ThemeVariables } from '@common/atoms/ThemeVariables/ThemeVariables.client';
import { LocaleID } from '@common/clients/api';
import { type ContextData, isContextData, type ResolvedContextData } from '@common/defaults/ContextData';
import { logger } from '@common/logger';
import { ThemeID } from '@common/types/ThemeID';
import { ContextDataProvider } from '@common/useContextData';
import { UserContextProvider } from '@common/useUserContext/UserContextProvider';

import '../styles/global.scss';

function MyApp({ Component, pageProps: props }: AppProps) {
    const contextData: ContextData | undefined = isContextData(props?.contextData)
        ? props.contextData
        : undefined;

    return (
        <>
            <Head>
                <ThemeFonts contextData={contextData} themeID={ThemeID.ADMIN} />
                <ThemeVariables themeID={ThemeID.ADMIN} />
            </Head>
            <I18nProvider locale={LocaleID.NL_NL} i18n={i18n}>
                {contextData && props.userContext ? (
                    <SupertokensWrapper
                        autoRefresh={true}
                        contextData={contextData}
                        userContext={props.userContext}
                    >
                        <ContextDataProvider contextData={contextData as ResolvedContextData}>
                            <UserContextProvider userContext={props.userContext}>
                                <ThemeProvider>
                                    <Navigation menu={props.menu} />
                                    <Component {...props} />
                                </ThemeProvider>
                            </UserContextProvider>
                            <FlashMessageHolder />
                        </ContextDataProvider>
                    </SupertokensWrapper>
                ) : (
                    <Component {...props} />
                )}
            </I18nProvider>
        </>
    );
}

export function reportWebVitals(metric: NextWebVitalsMetric) {
    logger.debug(metric);
    if (typeof global.window.gtag !== 'undefined') {
        window.gtag('event', metric.name, {
            event_category: metric.label === 'web-vital' ? 'Web Vitals' : 'Next.js custom metric',
            value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
            event_label: metric.id,
            non_interaction: true,
        });
    }
}

export default MyApp;
